import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react"
import { withStyles } from "@mui/styles"
import { styled } from "@mui/material/styles"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Grid,
  Divider,
  Link,
} from "@mui/material"
import { FormattedMessage, useIntl } from "react-intl"
import { useDispatch } from "react-redux"
import clsx from "clsx"
import useApiLoading from "hooks/useApiLoading"
import useAgreements from "hooks/useAgreements"
import { backgroundFixed } from "utils/backgroundFixed.js"
import PinkSquareCheckBox from "atoms/button/PinkSquareCheckBox"
import ModalDisabledAgreementButton from "molecules/ModalDisabledAgreementButton"
import { putOverallAgreements } from "actions"

// 許諾同意状態
const OVERALL_AGREEMENTS = {
  TRUE: "1",
  FALSE: "0",
}

const BoxDiv = styled("div")(({ theme }) => ({
  height: "65px",
  marginTop: "8px",
  borderRadius: "4px",
  border: "solid 1px #dfe0e0",
  display: "flex",
  cursor: "pointer",
  color: "rgba(0, 0, 0, 0.87)",
  backgroundColor: "#FFF",
  [theme.breakpoints.down("sm")]: {
    padding: "0 2px 0 15px",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "0 2px 0 22px",
  },
}))

const TextDiv = styled("div")(({ theme }) => ({
  fontFamily: "HiraKakuProN-W3",
  whiteSpace: "pre-wrap",
  fontSize: "18px",
  [theme.breakpoints.down("xs")]: {
    fontSize: "14px",
  },
}))

const StyledHeader = styled("div")(({ theme }) => ({
  "& .MuiTypography-root": {
    wordBreak: "normal",
    color: "#333",
    fontWeight: 900,
    textAlign: "left",
    lineHeight: "2",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "20px",
    },
  },
}))

const StyledDivider = styled(Divider)({
  height: "2px",
  backgroundColor: "#e8e8e8",
})

const StyledContent = styled(DialogContent)(({ theme }) => ({
  [theme.breakpoints.down("xs")]: {
    maxHeight: "calc(100vh - 200px)",
  },
  whiteSpace: "pre-line",
  wordBreak: "normal",
  paddingLeft: "23px",
  paddingRight: "23px",
  paddingTop: "0",
  paddingBottom: "30px",
}))

const ContentTitle = styled("div")(({ theme }) => ({
  color: "#333",
  fontWeight: 800,
  lineHeight: "18px",
  marginBottom: "10px",
  textIndent: "-10px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "16px",
  },
}))

const ContentFont = styled(DialogContentText)(({ theme }) => ({
  color: "#333",
  lineHeight: "18px",
  marginBottom: "20px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "14px",
  },
}))

const ListBox = styled("ul")({
  listStyle: "none",
  padding: "0 0 0 15px",
  margin: "0",
})

const ListText = styled("li")(({ theme }) => ({
  position: "relative",
  color: "#333",
  lineHeight: "18px",
  marginBottom: "10px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "14px",
  },
  "&:before": {
    content: "'・'",
    position: "absolute",
    top: "-1px",
    left: "-16px",
  },
}))

const AsteriskText = styled(ListText)({
  "&:before": {
    content: "'※'",
    position: "absolute",
    top: "-1px",
    left: "-16px",
  },
})

const CheckboxText = styled(Grid)(({ theme }) => ({
  color: "#333",
  lineHeight: "18px",
  marginBottom: "20px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "14px",
  },
}))

const LeftBox = styled("div")({
  width: "80%",
})

const RightBox = styled("div")({
  width: "20%",
  textAlign: "right",
})

const StyledDialogActions = styled(DialogActions)({
  padding: "14px",
  textAlign: "center",
})

const StyledDialog = withStyles({
  paperFullWidth: {
    maxWidth: "650px",
    width: "calc(100vw - 30px)",
  },
  paper: {
    marginLeft: "15px",
    marginRight: "15px",
    marginTop: "34px",
    borderRadius: "8px",
  },
})(Dialog)

const OverallAgreementsModal = forwardRef((props, ref) => {
  const dispatch = useDispatch()
  const { modalParam } = props
  useImperativeHandle(ref, () => ({
    handleClose,
    handleCloseBackground,
    clickButtonParam,
  }))
  const {
    title,
    textTop,
    linkText,
    textMid1,
    textCheckbox,
    textMid2,
    okButton,
    dataGaState,
  } = modalParam
  const [checked, setChecked] = useState(false)
  const { isApiLoading } = useApiLoading()
  const {
    overallAgreements,
    storeOverallAgreements,
    storeTopAgreementsDialogIsOpen,
  } = useAgreements()
  const typeMode = {
    disableTypography: false,
  }

  const handleClose = () => {
    storeTopAgreementsDialogIsOpen(false)
  }
  const handleCloseBackground = (event, reason) => {
    if (reason === "backdropClick") return
    storeTopAgreementsDialogIsOpen(false)
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: "TopAgreementsBackgroundClose",
    })
  }
  const intl = useIntl()
  const clickButtonParam = {
    id: "modalChangeButton",
    value: intl.formatMessage({ id: okButton }),
    disabled: isApiLoading || !checked,
    ga: dataGaState + "-ok-",
    handleClickButton: () => {
      dispatch(putOverallAgreements())
      storeOverallAgreements(OVERALL_AGREEMENTS.TRUE)
      handleClose()
    },
  }

  useEffect(() => {
    // unmount時
    return () => {
      storeTopAgreementsDialogIsOpen(false)
      backgroundFixed(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <StyledDialog
        fullWidth
        open={overallAgreements.topAgreementsDialogIsOpen}
        onClose={handleCloseBackground}
        TransitionProps={{
          onEnter: () => backgroundFixed(true),
          onExited: () => backgroundFixed(false),
        }}
        aria-labelledby="TopAgreementsTitle"
        id="TopAgreements"
      >
        <StyledHeader>
          <DialogTitle {...typeMode} id="TopAgreementsTitle">
            <FormattedMessage id={title} />
          </DialogTitle>
        </StyledHeader>
        <StyledContent>
          <ContentFont>
            <FormattedMessage id={textTop} />
          </ContentFont>
          <ContentFont>
            <Link
              href={process.env.REACT_APP_AGREEMENT_URL}
              target="_blank"
              underline="hover"
            >
              <FormattedMessage id={linkText} />
            </Link>
          </ContentFont>
          <CheckboxText container direction="row" alignItems="center">
            <PinkSquareCheckBox
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
            />
            <Grid item xs>
              <FormattedMessage id={textCheckbox} />
            </Grid>
          </CheckboxText>
          <ListBox>
            <AsteriskText>
              <FormattedMessage id={textMid1} />
            </AsteriskText>
            <AsteriskText>
              <FormattedMessage id={textMid2} />
            </AsteriskText>
          </ListBox>
        </StyledContent>
        <StyledDivider />
        <StyledDialogActions>
          <Grid container direction="row" justifyContent="center">
            <Grid item xs={6} sm={4}>
              <ModalDisabledAgreementButton {...clickButtonParam} />
            </Grid>
          </Grid>
        </StyledDialogActions>
      </StyledDialog>
    </>
  )
})

export default OverallAgreementsModal
